import { ReactNode, createContext } from 'react';
import { PlaycastFunctions } from '../api';
import { useStoreListener } from '../useStoreListener';
import type { StateStore } from '@playcastdotio/CoreTypes';
import { HostMacroRunningIndicator } from './HostMacroRunningIndicator';

type LocalProps = {
  functions: PlaycastFunctions;
  children?: ReactNode;
};

type UnwrapStateStores<T> = {
  [P in keyof T]: T[P] extends StateStore<infer U> ? U : never;
};

export type PlaycastReactFunctions = Omit<PlaycastFunctions, 'stateStores'> & {
  stateStores: UnwrapStateStores<PlaycastFunctions['stateStores']>;
};

export const PlaycastContext = createContext<PlaycastReactFunctions>({} as PlaycastReactFunctions);

export const PlaycastWrapper = (props: LocalProps) => {
  const { functions, children } = props;

  const stateStores: UnwrapStateStores<PlaycastFunctions['stateStores']> = {
    adModeState: useStoreListener(functions.stateStores.adModeState),
    activeShadowcamState: useStoreListener(functions.stateStores.activeShadowcamState),
    shadowcamBindState: useStoreListener(functions.stateStores.shadowcamBindState),
    afkPreventionState: useStoreListener(functions.stateStores.afkPreventionState),
    apiState: useStoreListener(functions.stateStores.apiState),
    audioState: useStoreListener(functions.stateStores.audioState),
    authState: useStoreListener(functions.stateStores.authState),
    automaticUpdatesEnabledState: useStoreListener(functions.stateStores.automaticUpdatesEnabledState),
    driverState: useStoreListener(functions.stateStores.driverState),
    displayState: useStoreListener(functions.stateStores.displayState),
    discordSettingsState: useStoreListener(functions.stateStores.discordSettingsState),
    functionConnectionState: useStoreListener(functions.stateStores.functionConnectionState),
    gamepadState: useStoreListener(functions.stateStores.gamepadState),
    guestAttributionState: useStoreListener(functions.stateStores.guestAttributionState),
    globalInputState: useStoreListener(functions.stateStores.globalInputState),
    hostGamepadState: useStoreListener(functions.stateStores.hostGamepadState),
    hostMatchmakingState: useStoreListener(functions.stateStores.hostMatchmakingState),
    hostInputDominanceState: useStoreListener(functions.stateStores.hostInputDominanceState),
    hostPauseInputState: useStoreListener(functions.stateStores.hostPauseInputState),
    hostQrCodeState: useStoreListener(functions.stateStores.hostQrCodeState),
    hostPresenceState: useStoreListener(functions.stateStores.hostPresenceState),
    hostRuleState: useStoreListener(functions.stateStores.hostRuleState),
    hostState: useStoreListener(functions.stateStores.hostState),
    hostSettingsState: useStoreListener(functions.stateStores.hostSettingsState),
    hostWebrtcStatsState: useStoreListener(functions.stateStores.hostWebrtcStatsState),
    iceServerState: useStoreListener(functions.stateStores.iceServerState),
    invitationSendState: useStoreListener(functions.stateStores.invitationSendState),
    invitationStateV2: useStoreListener(functions.stateStores.invitationStateV2),
    knockMetadataState: useStoreListener(functions.stateStores.knockMetadataState),
    loginProfileState: useStoreListener(functions.stateStores.loginProfileState),
    lobbyStateV2: useStoreListener(functions.stateStores.lobbyStateV2),
    machineCredentialsState: useStoreListener(functions.stateStores.machineCredentialsState),
    machineIdState: useStoreListener(functions.stateStores.machineIdState),
    machineIdAttestationReplyState: useStoreListener(functions.stateStores.machineIdAttestationReplyState),
    nonsoleReadinessState: useStoreListener(functions.stateStores.nonsoleReadinessState),
    hostModeEntryResultState: useStoreListener(functions.stateStores.hostModeEntryResultState),
    machineNameState: useStoreListener(functions.stateStores.machineNameState),
    matchmakerOverrideState: useStoreListener(functions.stateStores.matchmakerOverrideState),
    matchmakerState: useStoreListener(functions.stateStores.matchmakerState),
    micPreviewState: useStoreListener(functions.stateStores.micPreviewState),
    networkMetricsState: useStoreListener(functions.stateStores.networkMetricsState),
    nodeServiceLoginTokenState: useStoreListener(functions.stateStores.nodeServiceLoginTokenState),
    notificationInAppState: useStoreListener(functions.stateStores.notificationInAppState),
    ownerAliasState: useStoreListener(functions.stateStores.ownerAliasState),
    ownerAuthIdState: useStoreListener(functions.stateStores.ownerAuthIdState),
    pauseInputState: useStoreListener(functions.stateStores.pauseInputState),
    playerActivityState: useStoreListener(functions.stateStores.playerActivityState),
    playerState: useStoreListener(functions.stateStores.playerState),
    playerStatState: useStoreListener(functions.stateStores.playerStatState),
    playjectorHostModeState: useStoreListener(functions.stateStores.playjectorHostModeState),
    playerNameState: useStoreListener(functions.stateStores.playerNameState),
    preferenceState: useStoreListener(functions.stateStores.preferenceState),
    processCatalogState: useStoreListener(functions.stateStores.processCatalogState),
    profileState: useStoreListener(functions.stateStores.profileState),
    qrCodeState: useStoreListener(functions.stateStores.qrCodeState),
    queueState: useStoreListener(functions.stateStores.queueState),
    regionOverrideState: useStoreListener(functions.stateStores.regionOverrideState),
    remoteHostList: useStoreListener(functions.stateStores.remoteHostList),
    runningApplicationState: useStoreListener(functions.stateStores.runningApplicationState),
    sanitizationState: useStoreListener(functions.stateStores.sanitizationState),
    servicePresenceState: useStoreListener(functions.stateStores.servicePresenceState),
    showStaticImageState: useStoreListener(functions.stateStores.showStaticImageState),
    steamInstallState: useStoreListener(functions.stateStores.steamInstallState),
    staticImageState: useStoreListener(functions.stateStores.staticImageState),
    twitchSettingsState: useStoreListener(functions.stateStores.twitchSettingsState),
    truncMachineIdState: useStoreListener(functions.stateStores.truncMachineIdState),
    userEventsState: useStoreListener(functions.stateStores.userEventsState),
    versionState: useStoreListener(functions.stateStores.versionState),
    videoState: useStoreListener(functions.stateStores.videoState),
    webrtcPresetGroups: useStoreListener(functions.stateStores.webrtcPresetGroups),
    qrCodeBase64State: useStoreListener(functions.stateStores.qrCodeBase64State),
    qrCodeGenerationState: useStoreListener(functions.stateStores.qrCodeGenerationState),
    gameClipConfigState: useStoreListener(functions.stateStores.gameClipConfigState),
    gameClipStatusState: useStoreListener(functions.stateStores.gameClipStatusState),
    windowState: useStoreListener(functions.stateStores.windowState),
    vpnInfoState: useStoreListener(functions.stateStores.vpnInfoState),
    machineIdAttestationState: useStoreListener(functions.stateStores.machineIdAttestationState),
    touchInputConfigState: useStoreListener(functions.stateStores.touchInputConfigState),
    userProfileState: useStoreListener(functions.stateStores.userProfileState),
  };
  const value = {
    ...functions,
    stateStores,
  };

  return (
    <PlaycastContext.Provider value={value}>
      <HostMacroRunningIndicator />
      {children}
    </PlaycastContext.Provider>
  );
};
